import { useEffect, useRef, useState } from 'react';

const MAX_RECORDING_TIME = 120; // 2 minute in seconds
const WARNING_SECONDS_LEFT = 20;
const DANGER_SECONDS_LEFT = 10;

export const useRecordingTimer = (isRecording: boolean, stopRecording?: VoidFunction) => {
	const [recordingTimer, setRecordingTimer] = useState(0);
	const [timerColor, setTimerColor] = useState<string>('transparent');
	const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const stopRecordingRef = useRef<VoidFunction | undefined>(stopRecording);

	// Update the ref whenever stopRecording changes
	useEffect(() => {
		stopRecordingRef.current = stopRecording;
	}, [stopRecording]);

	useEffect(() => {
		if (isRecording) {
			setRecordingTimer(0);
		}
	}, [isRecording]);

	useEffect(() => {
		if (isRecording && recordingTimer < MAX_RECORDING_TIME) {
			// If recording is active and timer hasn't reached MAX_RECORDING_TIME, start the interval
			timerIntervalRef.current = setInterval(() => {
				setRecordingTimer((prevTime) => {
					if (prevTime < MAX_RECORDING_TIME - 1) {
						return prevTime + 1;
					}
					if (stopRecordingRef.current) stopRecordingRef.current();
					if (timerIntervalRef.current) {
						clearInterval(timerIntervalRef.current);
					}
					return MAX_RECORDING_TIME;
				});
			}, 1000);
		} else if (!isRecording) {
			if (timerIntervalRef.current) {
				clearInterval(timerIntervalRef.current);
			}
		}

		return () => {
			if (timerIntervalRef.current) {
				clearInterval(timerIntervalRef.current);
			}
		};
	}, [isRecording, recordingTimer]);

	const formattedTime = (recordingTimer: number) => {
		const minutes = Math.floor(recordingTimer / 60);
		const seconds = recordingTimer % 60;
		return `${String(minutes).padStart(1, '0')}:${String(seconds).padStart(2, '0')}`;
	};

	useEffect(() => {
		if (recordingTimer >= MAX_RECORDING_TIME - DANGER_SECONDS_LEFT) {
			setTimerColor('red.400');
		} else if (recordingTimer >= MAX_RECORDING_TIME - WARNING_SECONDS_LEFT) {
			setTimerColor('yellow.400');
		} else {
			setTimerColor('transparent');
		}
	}, [recordingTimer]);

	return {
		formattedTime: formattedTime(recordingTimer),
		totalTime: formattedTime(MAX_RECORDING_TIME),
		timerColor,
	};
};
